import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customButtonsClient } from 'api';
import type {
  CustomActionButtonToAdd,
  CustomActionButtonToEdit,
  ExecuteCustomActionButtonResponse,
  GetCustomActionButtonsResult,
  GetUserCustomActionButtonsResult
} from 'api/models/mainservice';
import type { ApiError } from 'common/models';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';

const customActionButtonsKey = 'customActionButtons';
const userCustomActionButtonsKey = 'userCustomActionButtons';

export const getCustomActionButtonsQueryKeyBuilder = () => [customActionButtonsKey];
export const getUserCustomActionButtonsQueryKeyBuilder = () => [userCustomActionButtonsKey];

export const useGetCustomActionButtonsQuery = () => {
  return useQuery<GetCustomActionButtonsResult, string>({
    queryKey: getCustomActionButtonsQueryKeyBuilder(),
    queryFn: customButtonsClient.getCustomActionButtons
  });
};

export const useGetUserCustomActionButtonsQuery = () => {
  return useQuery<GetUserCustomActionButtonsResult, string>({
    queryKey: getUserCustomActionButtonsQueryKeyBuilder(),
    queryFn: customButtonsClient.getUserCustomActionButtons
  });
};

export const useAddCustomActionButtonMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, string, CustomActionButtonToAdd>({
    mutationFn: customButtonsClient.addCustomActionButton,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCustomActionButtonsQueryKeyBuilder() });
      await queryClient.invalidateQueries({
        queryKey: getUserCustomActionButtonsQueryKeyBuilder()
      });
    }
  });
};

export const useEditCustomActionButtonMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, string, { id: string; data: CustomActionButtonToEdit }>({
    mutationFn: customButtonsClient.editCustomActionButton,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCustomActionButtonsQueryKeyBuilder() });
      await queryClient.invalidateQueries({
        queryKey: getUserCustomActionButtonsQueryKeyBuilder()
      });
    }
  });
};

export const useDeleteCustomActionButtonMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, string, string>({
    mutationFn: customButtonsClient.deleteCustomActionButton,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCustomActionButtonsQueryKeyBuilder() });
      await queryClient.invalidateQueries({
        queryKey: getUserCustomActionButtonsQueryKeyBuilder()
      });
    }
  });
};

export const useExecuteCustomActionButtonMutation = (isTest?: boolean) => {
  return useMutation<ExecuteCustomActionButtonResponse, ApiError, string>({
    mutationFn: (buttonId) => customButtonsClient.executeCustomActionButton(buttonId, isTest),
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useExecuteCustomActionButtonOnEntityMutation = () => {
  return useMutation<ExecuteCustomActionButtonResponse, ApiError, { id: string; entityId: string }>(
    {
      mutationFn: customButtonsClient.executeCustomActionButtonOnEntity,
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};
